import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import { applyGanttDateChange, fetchGanttData } from './ganttApi';
import { buildGanttRows, createGanttDatePatch, formatGanttDay, ganttIntervalLabel, getGanttColumns, isGanttRowVisible, overlapsGanttRange, parseGanttDay, patchGanttData, setGanttDataVersion, startOfGanttWeek, summarizeHiddenGanttRows } from './ganttUtils';
import './GanttChart.css';

export default function GanttChart() {
    const timezone = localStorage.getItem('timezone') || moment.tz.guess();
    const [today, setToday] = useState(() => parseGanttDay(new Date(), timezone));
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [mutationError, setMutationError] = useState('');
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);
    const [mode, setMode] = useState('day');
    const [anchor, setAnchor] = useState(today - 7);
    const [selection, setSelection] = useState(null);
    const [editPreview, setEditPreview] = useState(null);
    const [savingTargets, setSavingTargets] = useState([]);
    const drag = useRef(null);
    const barDrag = useRef(null);
    const header = useRef(null);
    const scroller = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        fetchGanttData(controller.signal).then(setData).catch(reason => {
            if (reason.name !== 'AbortError') setError(reason.message);
        }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [revision]);

    useEffect(() => {
        const timer = setInterval(() => setToday(parseGanttDay(new Date(), timezone)), 60000);
        const escape = event => {
            if (event.key === 'Escape') {
                drag.current = null;
                barDrag.current = null;
                setEditPreview(null);
                setSelection(null);
            }
        };
        window.addEventListener('keydown', escape);
        return () => { clearInterval(timer); window.removeEventListener('keydown', escape); };
    }, [timezone]);

    const rows = useMemo(() => buildGanttRows(data?.tasks || [], data?.todos || [], today, data?.todoVersion, timezone), [data, today, timezone]);
    const start = mode === 'week' ? startOfGanttWeek(anchor) : anchor;
    const columns = getGanttColumns(start, mode);
    const end = columns[columns.length - 1].end;
    const days = end - start + 1;
    const columnWidth = mode === 'day' ? 54 : 112;
    const width = columns.length * columnWidth;
    const visibleRows = rows.filter(row => isGanttRowVisible(row, start, end));
    const visibleTaskRows = visibleRows.filter(row => row.kind !== 'todo');
    const visibleTodoRows = visibleRows.filter(row => row.kind === 'todo');
    const hiddenSummary = summarizeHiddenGanttRows(rows, start, end);
    const matches = row => selection && row.intervals.some(interval => overlapsGanttRange(interval, selection));
    const matchCount = visibleRows.filter(matches).length;
    const clearSelection = () => { drag.current = null; setSelection(null); };
    const goTo = day => {
        drag.current = null;
        setAnchor(day);
        if (scroller.current) scroller.current.scrollLeft = 0;
    };
    const indexAt = clientX => Math.max(0, Math.min(columns.length - 1,
        Math.floor((clientX - header.current.getBoundingClientRect().left) / columnWidth)));
    const selectTo = index => {
        const first = Math.min(drag.current.index, index);
        const last = Math.max(drag.current.index, index);
        setSelection({ start: columns[first].start, end: columns[last].end });
    };
    const onPointerDown = event => {
        if (event.button !== 0) return;
        event.preventDefault();
        const index = indexAt(event.clientX);
        drag.current = { index, pointerId: event.pointerId };
        event.currentTarget.setPointerCapture(event.pointerId);
        selectTo(index);
    };
    const onPointerMove = event => {
        if (!drag.current || drag.current.pointerId !== event.pointerId) return;
        const bounds = scroller.current.getBoundingClientRect();
        if (event.clientX > bounds.right - 36) scroller.current.scrollLeft += columnWidth / 2;
        else if (event.clientX < bounds.left + 280) scroller.current.scrollLeft -= columnWidth / 2;
        selectTo(indexAt(event.clientX));
    };
    const onPointerUp = event => {
        if (!drag.current) return;
        selectTo(indexAt(event.clientX));
        drag.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };
    const selectWithKeyboard = (event, index) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setSelection(event.shiftKey && selection
                ? { start: Math.min(selection.start, columns[index].start), end: Math.max(selection.end, columns[index].end) }
                : { start: columns[index].start, end: columns[index].end });
        }
    };
    const position = (from, until) => {
        const visibleFrom = Math.max(start, from);
        const visibleUntil = Math.min(end, until);
        if (visibleFrom > visibleUntil) return { left: `${from > end ? 100 : 0}%`, width: 0 };
        return { left: `${(visibleFrom - start) / days * 100}%`, width: `${(visibleUntil - visibleFrom + 1) / days * 100}%` };
    };
    const selectionBand = selection && selection.start <= end && selection.end >= start
        ? <span className="gantt-selection-band" style={position(selection.start, selection.end)} /> : null;
    const todayLine = today >= start && today <= end
        ? <span className="gantt-today-line" style={{ left: `${(today - start + 0.5) / days * 100}%` }} /> : null;

    const targetKey = row => row.kind === 'todo' ? 'todos' : `task:${row.identity?.taskPracticalId}`;
    const hasMutationIdentity = row => ({
        task: Boolean(row.identity?.taskPracticalId),
        subtask: Boolean(row.identity?.taskPracticalId && row.identity?.sn),
        subsubtask: Boolean(row.identity?.taskPracticalId && row.identity?.subtaskSn && row.identity?.sn),
        todo: Boolean(row.identity?.todoId),
    }[row.kind]);
    const isEditable = row => !row.derived && row.intervals.length === 1 && hasMutationIdentity(row)
        && !savingTargets.includes(targetKey(row));
    const rangeForPointer = (session, clientX) => {
        const step = mode === 'week' ? 7 : 1;
        const delta = Math.round((clientX - session.originX) / columnWidth) * step;
        let nextStart = session.start;
        let nextEnd = session.end;
        if (session.action === 'move') {
            // A missing start (drawn from today) or end (open) is not saved, so it does not move either.
            if (!session.fixedStart) nextStart += delta;
            if (!session.fixedEnd) nextEnd += delta;
            nextEnd = Math.max(nextEnd, nextStart);
        } else if (session.action === 'resize-start') {
            nextStart = Math.min(session.start + delta, session.end);
        } else {
            nextEnd = Math.max(session.end + delta, session.start);
        }
        return { start: nextStart, end: nextEnd, moved: delta !== 0 };
    };
    const beginBarDrag = (event, row, intervalIndex, action) => {
        if (event.button !== 0 || !isEditable(row) || (row.kind === 'todo' && action !== 'move')) return;
        event.preventDefault();
        event.stopPropagation();
        const interval = row.intervals[intervalIndex];
        const displayStart = interval.start;
        const displayEnd = interval.end;
        barDrag.current = {
            row, intervalIndex, action, pointerId: event.pointerId, originX: event.clientX,
            start: displayStart, end: displayEnd, moved: false, captureTarget: event.currentTarget,
            fixedStart: interval.from === null && !interval.expired, fixedEnd: interval.until === null,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        setEditPreview({ rowKey: row.key, intervalIndex, action, start: displayStart, end: displayEnd });
    };
    const moveBarDrag = event => {
        const session = barDrag.current;
        if (!session || session.pointerId !== event.pointerId) return;
        event.preventDefault();
        const next = rangeForPointer(session, event.clientX);
        session.moved = session.moved || next.moved;
        session.nextStart = next.start;
        session.nextEnd = next.end;
        setEditPreview({ rowKey: session.row.key, intervalIndex: session.intervalIndex, action: session.action, start: next.start, end: next.end });
    };
    const saveBarChange = async (row, action, nextStart, nextEnd) => {
        const patch = createGanttDatePatch(row, action, nextStart, nextEnd, timezone);
        if (!patch) return;
        const rollback = Object.fromEntries(Object.keys(patch).map(field => [field, row.sourceDates[field]]));
        const savingKey = targetKey(row);
        setMutationError('');
        setSavingTargets(current => [...current, savingKey]);
        setData(current => patchGanttData(current, row, patch));
        try {
            const result = await applyGanttDateChange(row, patch);
            setData(current => setGanttDataVersion(current, row, result.version));
        } catch (reason) {
            setData(current => patchGanttData(current, row, rollback));
            setMutationError(reason.message || 'Could not save the timeline change.');
        } finally {
            setSavingTargets(current => current.filter(key => key !== savingKey));
        }
    };
    const finishBarDrag = event => {
        const session = barDrag.current;
        if (!session || session.pointerId !== event.pointerId) return;
        const next = rangeForPointer(session, event.clientX);
        const shouldSave = session.moved || next.moved;
        if (session.captureTarget.hasPointerCapture(event.pointerId)) session.captureTarget.releasePointerCapture(event.pointerId);
        barDrag.current = null;
        setEditPreview(null);
        if (shouldSave) saveBarChange(session.row, session.action, next.start, next.end);
    };
    const cancelBarDrag = event => {
        const session = barDrag.current;
        if (session?.captureTarget?.hasPointerCapture(event.pointerId)) session.captureTarget.releasePointerCapture(event.pointerId);
        barDrag.current = null;
        setEditPreview(null);
    };

    const renderRow = row => {
        const visible = row.intervals.filter(interval => !interval.invalid && interval.start <= end && interval.end >= start);
        const label = row.derived ? 'Summary of dated descendants' : row.intervals.map(ganttIntervalLabel).join('; ') || 'No dates';
        const state = selection ? (matches(row) ? 'is-match' : 'is-muted') : '';
        const editable = isEditable(row);
        const saving = savingTargets.includes(targetKey(row));
        return <div role="row" key={row.key} className={`gantt-row gantt-${row.kind} ${state} ${saving ? 'is-saving' : ''}`} data-testid={`gantt-row-${row.key}`}>
            <div role="rowheader" className="gantt-label" style={{ paddingLeft: 18 + row.depth * 18 }} title={`${row.name} · ${label}`}>
                <span className="gantt-kind-dot" />
                <span className="gantt-row-title"><strong>{row.name}</strong><small>{row.kind === 'subsubtask' ? 'Subsubtask · ' : ''}{label}</small></span>
                {selection && matches(row) && <span className="gantt-match-mark" aria-label="Matches selected range">●</span>}
            </div>
            <div role="cell" className="gantt-track" aria-label={label}>
                {selectionBand}{todayLine}
                {visible.map((interval, index) => {
                    const preview = editPreview?.rowKey === row.key && editPreview.intervalIndex === index ? editPreview : null;
                    const displayStart = preview?.start ?? interval.start;
                    const displayEnd = preview?.end ?? interval.end;
                    const formatEnd = (day, format) => Number.isFinite(day) ? formatGanttDay(day, format) : 'no end';
                    const displayLabel = preview ? `${formatGanttDay(displayStart)} – ${formatEnd(displayEnd)}` : ganttIntervalLabel(interval);
                    const showStartHandle = interval.from === null || interval.start >= start;
                    const showEndHandle = interval.until !== null && interval.end <= end;
                    return <span key={index}
                    className={`gantt-bar ${interval.open && !preview ? 'is-open' : ''} ${interval.expired ? 'is-expired' : ''} ${row.derived ? 'is-summary' : ''} ${editable ? 'is-editable' : ''} ${preview ? 'is-dragging' : ''}`}
                    style={row.kind === 'todo' && mode === 'week'
                        ? position(startOfGanttWeek(displayStart), startOfGanttWeek(displayStart) + 6)
                        : position(displayStart, displayEnd)} title={`${row.name}: ${displayLabel}`}
                    onPointerDown={event => beginBarDrag(event, row, index, 'move')} onPointerMove={moveBarDrag}
                    onPointerUp={finishBarDrag} onPointerCancel={cancelBarDrag}>
                    {!row.derived && <span>{preview
                        ? row.kind === 'todo'
                            ? `Move to ${formatGanttDay(displayStart, 'ddd DD/MM')}`
                            : `${formatGanttDay(displayStart, 'DD/MM')} → ${formatEnd(displayEnd, 'DD/MM')}`
                        : <>{row.name}{interval.open ? ' →' : ''}</>}</span>}
                    {editable && row.kind !== 'todo' && <>
                        {showStartHandle &&
                        <button type="button" className="gantt-resize-handle is-start" aria-label={`Change start date for ${row.name}`}
                            onPointerDown={event => beginBarDrag(event, row, index, 'resize-start')} />}
                        {showEndHandle &&
                        <button type="button" className="gantt-resize-handle is-end" aria-label={`Change end date for ${row.name}`}
                            onPointerDown={event => beginBarDrag(event, row, index, 'resize-end')} />}
                    </>}
                </span>;
                })}
            </div>
        </div>;
    };
    const hiddenSummaryDetails = [
        hiddenSummary.latestBefore !== null ? `latest: ${formatGanttDay(hiddenSummary.latestBefore, 'DD/MM')}` : null,
        hiddenSummary.earliestAfter !== null ? `earliest: ${formatGanttDay(hiddenSummary.earliestAfter, 'DD/MM')}` : null,
    ].filter(Boolean).join(' · ') || 'no valid dates';
    const hiddenSummaryRow = hiddenSummary.count > 0 && <div role="row" className="gantt-hidden-summary" data-testid="gantt-hidden-summary">
        <div role="rowheader" className="gantt-label">
            <strong>{hiddenSummary.count} {hiddenSummary.count === 1 ? 'row' : 'rows'} hidden</strong>
            <small>{hiddenSummaryDetails}</small>
        </div>
        <div role="cell" className="gantt-track"><span>Outside this view</span></div>
    </div>;
    const section = (title, subtitle) => <div role="row" className="gantt-section"><div role="rowheader" className="gantt-label"><strong>{title}</strong><small>{subtitle}</small></div><div role="cell" className="gantt-track">{selectionBand}{todayLine}</div></div>;

    return <main className="gantt-page">
        <header className="gantt-page-header">
            <div><Link className="gantt-back" to="/">← Home</Link><p className="gantt-eyebrow">PLANNING / TIMELINE</p><h1>Gantt chart</h1><p>Every task, one timeline.</p></div>
            <div className="gantt-modes" role="group" aria-label="Timeline resolution">
                {['day', 'week'].map(value => <button key={value} aria-pressed={mode === value} onClick={() => { drag.current = null; setMode(value); if (scroller.current) scroller.current.scrollLeft = 0; }}>{value === 'day' ? 'Day Resolution' : 'Week Resolution'}</button>)}
            </div>
        </header>
        <div className="gantt-toolbar">
            <div className="gantt-navigation"><button aria-label="Previous period" onClick={() => goTo(start - days)}>←</button><button onClick={() => goTo(today - (mode === 'day' ? 7 : 14))}>Today</button><button aria-label="Next period" onClick={() => goTo(start + days)}>→</button><strong>{formatGanttDay(start)} – {formatGanttDay(end)}</strong></div>
            <div className="gantt-jump"><label htmlFor="gantt-jump-date">Jump to</label><input id="gantt-jump-date" type="date" value={formatGanttDay(anchor, 'YYYY-MM-DD')} onChange={event => { const day = parseGanttDay(event.target.value); if (day !== null) goTo(day); }} /><button onClick={() => setRevision(value => value + 1)} disabled={loading}>Refresh</button></div>
        </div>
        <div className="gantt-selection-info" role="status">
            {selection ? <><strong>{formatGanttDay(selection.start)} – {formatGanttDay(selection.end)}</strong><span>{matchCount} matching rows</span><button onClick={clearSelection}>Clear selection <kbd>Esc</kbd></button></>
                : <span>Drag across the date headers to highlight overlapping eligibility. Press <kbd>Esc</kbd> to clear.</span>}
        </div>
        {error && <div className="gantt-message" role="alert">{error} <button onClick={() => setRevision(value => value + 1)}>Retry</button></div>}
        {mutationError && <div className="gantt-save-error" role="alert">{mutationError} <button onClick={() => setMutationError('')}>Dismiss</button></div>}
        {loading && <div className="gantt-message" role="status">Loading timeline…</div>}
        {!loading && !error && data && <>
            <div className="gantt-scroll" ref={scroller} tabIndex={0} aria-label="Scrollable Gantt timeline">
                <div role="table" aria-label="Tasks and todos timeline" className="gantt-table" style={{ '--timeline-width': `${width}px`, '--column-width': `${columnWidth}px` }}>
                    <div role="row" className="gantt-date-row">
                        <div role="columnheader" className="gantt-label"><strong>Task / eligibility</strong><small>{visibleRows.length} visible · {hiddenSummary.count} hidden · {mode === 'day' ? 'Daily' : 'Weekly'} view</small></div>
                        <div className="gantt-date-cells" ref={header} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={clearSelection} onLostPointerCapture={() => { drag.current = null; }}>
                            {columns.map((column, index) => <button role="columnheader" key={column.start} onKeyDown={event => selectWithKeyboard(event, index)}
                                aria-label={`${formatGanttDay(column.start)}${mode === 'week' ? ` to ${formatGanttDay(column.end)}` : ''}`}
                                aria-selected={Boolean(selection && column.start <= selection.end && column.end >= selection.start)}
                                className={`${column.start <= today && column.end >= today ? 'is-today' : ''} ${mode === 'day' && [0, 6].includes(new Date(column.start * 86400000).getUTCDay()) ? 'is-weekend' : ''}`}>
                                <small>{formatGanttDay(column.start, mode === 'day' ? 'MMM' : 'MMM YYYY')}</small><strong>{formatGanttDay(column.start, 'D')}{mode === 'week' ? `–${formatGanttDay(column.end, 'D')}` : ''}</strong><small>{mode === 'day' ? formatGanttDay(column.start, 'ddd') : 'Mon–Sun'}</small>
                            </button>)}
                        </div>
                    </div>
                    {hiddenSummaryRow}
                    {section('SUBTASKS', `${visibleTaskRows.length} visible · subsubtasks nested below`)}
                    {visibleTaskRows.map(renderRow)}
                    {!data.tasks.length && <div className="gantt-empty-section">No tasks yet.</div>}
                    {section('TODOS', `${visibleTodoRows.length} visible · ${data.todos.length} total`)}
                    {visibleTodoRows.map(renderRow)}
                    {!data.todos.length && <div className="gantt-empty-section">No todos yet.</div>}
                </div>
            </div>
            <footer className="gantt-legend"><span><i />Bounded eligibility</span><span><i className="is-open" />No end date</span><span><i className="is-summary" />Task summary</span><span><i className="is-expired" />Expired deadline</span><span className="gantt-legend-note">Drag a bar to move it; drag either handle to resize. Todos move only. Weeks move in whole Monday–Sunday blocks. End dates save at 02:00 the following day.</span></footer>
        </>}
    </main>;
}
